<template>
	<div>
		<div class="display-1 font-weight-light mb-3">Active Workers</div>
		<timezone />
		<v-row >
			<v-col class="shrink" style="align-items: center;justify-content: center;display: flex;">
				<v-progress-circular :indeterminate="refreshing || !loaded" color="lime" :size="30"></v-progress-circular>
			</v-col>
			<v-col class="grow pl-2" >
				<v-switch v-model="autoRefresh" :label="refreshLabel" ></v-switch>
			</v-col>
		</v-row>

		<v-alert
			:value="restartWarning"
			type="warning"
			dismissible
		>
			After restart is complete the watcher must also be started. It will be auto-restarted after 5 minutes.
		</v-alert>

		<div v-if="loaded">
			<v-card flat class="mb-3" v-for="set in scaleSets" :key="set.name">
				<v-toolbar flat dense color="primary">
					<v-toolbar-title class="title white--text" style="margin: 0 10px">{{set.name}}</v-toolbar-title>
				</v-toolbar>

				<v-divider></v-divider>

				<v-card-text>
					<v-btn color="primary" class="mr-3" title="Reimage All Instances" small outlined rounded @click="setAction(set, 'Reimage')">
						<v-icon small class="spacedlg">far fa-save</v-icon>
						Reimage All Instances
					</v-btn>
					<v-btn color="primary" class="mr-3" title="Restart All Instances" small outlined rounded @click="setAction(set, 'Restart')">
						<v-icon small class="spacedlg">far fa-sync-alt</v-icon>
						Restart All Instances
					</v-btn>
					<v-btn color="primary" title="Start All Watchers" small outlined rounded @click="setAction(set, 'Watch')">
						<v-icon small class="spacedlg">far fa-play</v-icon>
						Start All Watchers
					</v-btn>

					<v-simple-table class="mt-3" v-if="set.workers.length > 0">
						<thead>
						<tr>
							<th class="column text-left">Instance</th>
							<!--							<th class="column text-xs-left">Name</th>-->
							<!--							<th class="column text-xs-left">Heartbeat</th>-->
							<th class="column text-left">Heartbeat Age</th>
							<th class="column text-left">Model</th>
							<th class="column text-left">Region</th>
							<th class="column text-left">Worker</th>
							<th class="column text-left">VM Status</th>
							<th class="column text-left">VM Status</th>
							<th class="column text-left">Actions</th>
						</tr>
						</thead>
						<tbody>
						<tr v-for="r in set.workers" :key="r.id">
							<td>{{r.instanceId}}</td>
							<!--							<td>{{r.computerName}}</td>-->
							<!--							<td>{{toTime(r.lastHeartbeat)}}</td>-->
							<td><workers-item-heartbeat :heartbeat="r.lastHeartbeat" :limit="heartbeatLimit" :time="currentTime" /></td>
							<td><workers-item-model :current="r.modelCurrent" /></td>
							<td>{{r.setRegion}}</td>
							<td style="white-space: nowrap">
								<v-btn text icon color="primary" title="View Log" outlined small @click="displayLogs(r)" class="mr-1">
									<v-icon small>far fa-list</v-icon>
								</v-btn>
								<workers-item-status :status="r.status" />
							</td>
							<td>{{r.machineStatus}}</td>
							<td>{{r.powerState}}</td>
							<td style="white-space: nowrap">
								<!--								<v-btn text icon color="primary" title="Update Model" outlined small @click="performAction(r, 'update')">-->
								<!--									<v-icon small>far fa-arrow-up</v-icon>-->
								<!--								</v-btn>-->
								<v-btn text icon color="primary" title="Reimage Server" outlined small @click="performAction(r, 'Reimage')" class="mr-1">
									<v-icon small>far fa-save</v-icon>
								</v-btn>
								<v-btn text icon color="primary" title="Restart Server" outlined small @click="performAction(r, 'Restart')" class="mr-1">
									<v-icon small>far fa-sync-alt</v-icon>
								</v-btn>
								<v-btn text icon color="primary" title="Start Watcher" outlined small @click="performAction(r, 'Watch')">
									<v-icon small>far fa-play</v-icon>
								</v-btn>
							</td>
						</tr>
						</tbody>
					</v-simple-table>
					<v-alert
						v-else
						type="info"
					>
						No workers
					</v-alert>
				</v-card-text>
			</v-card>
		</div>

		<v-navigation-drawer
			temporary
			right
			v-model="showLogs"
			absolute
			>
			<v-card v-if="logsWorker">
				<v-toolbar color="light-blue" dark>
					<v-toolbar-title>{{logsWorker.instanceId}} : {{logsWorker.machineName}}</v-toolbar-title>
				</v-toolbar>
				<v-subheader>Last 20 Events</v-subheader>
				<v-list two-line subheader>
					<v-list-item
						v-for="item in logsWorker.logs"
						:key="item.id"
					>
						<v-list-item-content>
							<v-list-item-title>{{ item.event }}</v-list-item-title>
							<v-list-item-subtitle>{{ formatDateTime(item.created) }}</v-list-item-subtitle>
						</v-list-item-content>
					</v-list-item>
				</v-list>
			</v-card>
		</v-navigation-drawer>
	</div>
</template>

<script>
import { DateTime } from "luxon";
import _ from "lodash";
import { transactionStatus } from "../enums";
import api from "../api";
import workersItemStatus from "../components/workers/workers-item-status.vue";
import workersItemModel from "../components/workers/workers-item-model.vue";
import workersItemHeartbeat from "../components/workers/workers-item-heartbeat.vue";
import timezone	from "../components/timezone.vue";

export default {
	components: {
		workersItemStatus,
		workersItemModel,
		workersItemHeartbeat,
		timezone,
	},
	data() {
		return {
			autoRefresh: false,
			intervalTimer: null,
			intervalSec: 10,
			intervalCountdown: 10,
			refreshing: false,
			scaleSets: [],
			heartbeatLimit: null,
			loaded: false,
			currentTime: null,
			showLogs: false,
			logsWorker: null,
			restartWarning: false,
		};
	},
	created() {
		this.loadStatus().then(() => {
			this.loaded = true;
			this.autoRefresh = true;
		});
	},
	watch: {
		autoRefresh(v) {
			if (v) {
				this.startInterval();
			} else {
				this.refreshing = false;
				this.stopInterval();
			}
		},
	},
	beforeDestroy() {
		if (this.intervalTimer) { clearInterval(this.intervalTimer); }
	},
	computed: {
		refreshLabel() {
			return !this.autoRefresh || this.refreshing ? "Auto-Refresh" : `Auto-Refresh in ${this.intervalCountdown}`;
		},
	},
	methods: {
		statusMap(s) {
			switch (s) {
			case transactionStatus.Queued:
				return "start";
			case transactionStatus.Completed:
				return "end";
			case transactionStatus.Error:
			case transactionStatus.ErrorWillRetry:
				return "failed";
			case transactionStatus.Unknown:
				return "unknown";
			default:
				return "processing";
			}
		},
		startInterval() {
			this.intervalCountdown = this.intervalSec;
			this.interval();
			this.intervalTimer = setInterval(this.interval, 1000);
		},
		stopInterval() {
			clearInterval(this.intervalTimer);
		},
		interval() {
			this.intervalCountdown -= 1;
			if (this.intervalCountdown === 0) {
				this.loadStatus();
			}
		},
		loadStatus() {
			this.refreshing = true;
			this.currentTime = DateTime.utc();
			if (this.autoRefresh) {
				this.stopInterval();
			}

			return api.workersStatus().then((r) => {
				const data = r.data;

				this.heartbeatLimit = data.heartbeatLimit;

				this.scaleSets = data.scaleSets.map(s => ({
					name: s,
					workers: _.sortBy(data.workers.filter(w => w.setName === s), w => w.instanceId),
				}));

				this.scaleSets.push = {
					name: "Missing in Azure",
					workers: data.workers.filter(w => w.MissingInAzure),
				};

				this.refreshing = false;
				if (this.autoRefresh) {
					this.startInterval();
				}
			}).catch((e) => {
				this.showError(e);
			});
		},
		setAction(set, action) {
			set.workers.forEach(w => this.performAction(w, action));
		},
		performAction(worker, action) {
			this.showToast("success", `Requested ${action}`);

			if (action === "Restart") {
				this.restartWarning = true;
			}

			if (action === "Watch") {
				this.restartWarning = false;
			}
			api.workerAction({
				setResourceId: worker.setId,
				instanceId: worker.instanceId,
				action,
				machineName: worker.machineName,
			})
				.then(() => {})
				.catch((e) => {
					this.showError(e);
				});
		},
		displayLogs(worker) {
			this.logsWorker = worker;
			this.showLogs = true;
		},
	},
};
</script>
